import { computed, onMounted, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import Swal from "sweetalert2";

import reportService, {
  type WarehouseExpenseReportItem,
  type WarehousePurchaseReportItem,
  type WarehouseQuotationReportItem,
  type WarehouseSaleReportItem,
} from "@/services/report";
import warehouseService, { type Warehouse } from "@/services/warehouse";

type WarehouseReportTab = "sale" | "purchase" | "quotation" | "expense";

type WarehouseReportFilters = {
  warehouse_id: number | null;
  from_date: string;
  to_date: string;
  per_page: number;
};

const createInitialFilters = (): WarehouseReportFilters => ({
  warehouse_id: null,
  from_date: "",
  to_date: "",
  per_page: 10,
});

type ExportHandler = (params: {
  warehouse_id?: string | number;
  from_date?: string;
  to_date?: string;
  search_query?: string;
}) => Promise<{ data: BlobPart }>;

export function useWarehouseReport() {
  const { t } = useI18n();
  const activeTab = ref<WarehouseReportTab>("sale");
  const warehouses = ref<Warehouse[]>([]);
  const sales = ref<WarehouseSaleReportItem[]>([]);
  const purchases = ref<WarehousePurchaseReportItem[]>([]);
  const quotations = ref<WarehouseQuotationReportItem[]>([]);
  const expenses = ref<WarehouseExpenseReportItem[]>([]);
  const loading = ref(false);
  const exporting = ref(false);
  const searchQuery = ref("");
  const currentPage = ref(1);
  const total = ref(0);
  const filters = reactive<WarehouseReportFilters>(createInitialFilters());
  const i18nScope = "warehouse_report_page";
  const pageTitle = `${i18nScope}.title`;

  const pagination = computed(() => ({
    current: currentPage.value,
    pageSize: filters.per_page,
    total: total.value,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "30", "50", "100"],
    showTotal: (total: number, range: [number, number]) =>
      t(`${i18nScope}.pagination_total`, { start: range[0], end: range[1], total }),
    locale: { items_per_page: t(`${i18nScope}.items_per_page`) },
  }));

  const summary = computed(() => {
    if (activeTab.value === "purchase") {
      const grandTotal = purchases.value.reduce((sum, item) => sum + Number(item.invoice_total ?? 0), 0);
      return { label: t(`${i18nScope}.summary_purchase_total`), value: grandTotal };
    }

    if (activeTab.value === "quotation") {
      const grandTotal = quotations.value.reduce((sum, item) => sum + Number(item.invoice_total ?? 0), 0);
      return { label: t(`${i18nScope}.summary_quotation_total`), value: grandTotal };
    }

    if (activeTab.value === "expense") {
      const grandTotal = expenses.value.reduce((sum, item) => sum + Number(item.amount ?? 0), 0);
      return { label: t(`${i18nScope}.summary_expense_total`), value: grandTotal };
    }

    const grandTotal = sales.value.reduce((sum, item) => sum + Number(item.invoice_total ?? 0), 0);
    return { label: t(`${i18nScope}.summary_sales_total`), value: grandTotal };
  });

  const commonParams = computed(() => ({
    page: currentPage.value,
    per_page: filters.per_page,
    search_query: searchQuery.value || undefined,
    warehouse_id: filters.warehouse_id || undefined,
    from_date: filters.from_date || undefined,
    to_date: filters.to_date || undefined,
  }));

  const fetchWarehouses = async () => {
    try {
      warehouses.value = await warehouseService.getWarehouses();
    } catch (error) {
      console.error("Error loading warehouses for warehouse report:", error);
    }
  };

  const assignResponse = (items: unknown[], response: { current_page: number; total: number }) => {
    currentPage.value = response.current_page;
    total.value = response.total;
    return items;
  };

  const fetchReport = async () => {
    loading.value = true;

    try {
      if (activeTab.value === "purchase") {
        const response = await reportService.getWarehousePurchaseReport(commonParams.value);
        purchases.value = assignResponse(response.data, response) as WarehousePurchaseReportItem[];
        return;
      }

      if (activeTab.value === "quotation") {
        const response = await reportService.getWarehouseQuotationReport(commonParams.value);
        quotations.value = assignResponse(response.data, response) as WarehouseQuotationReportItem[];
        return;
      }

      if (activeTab.value === "expense") {
        const response = await reportService.getWarehouseExpenseReport(commonParams.value);
        expenses.value = assignResponse(response.data, response) as WarehouseExpenseReportItem[];
        return;
      }

      const response = await reportService.getWarehouseSaleReport(commonParams.value);
      sales.value = assignResponse(response.data, response) as WarehouseSaleReportItem[];
    } catch (error) {
      console.error("Error fetching warehouse report:", error);
      Swal.fire({
        icon: "error",
        title: t(`${i18nScope}.load_failed`),
        text: t(`${i18nScope}.unable_to_load_report`),
      });
    } finally {
      loading.value = false;
    }
  };

  const setActiveTab = (tab: WarehouseReportTab) => {
    if (activeTab.value === tab) return;

    activeTab.value = tab;
    currentPage.value = 1;
    fetchReport();
  };

  const handleTableChange = (page: number, pageSize: number) => {
    currentPage.value = page;

    if (pageSize !== filters.per_page) {
      filters.per_page = pageSize;
      currentPage.value = 1;
    }

    fetchReport();
  };

  const applyFilters = () => {
    currentPage.value = 1;
    fetchReport();
  };

  const resetFilters = () => {
    Object.assign(filters, createInitialFilters());
    currentPage.value = 1;
    fetchReport();
  };

  const exportHandlers: Record<WarehouseReportTab, { name: string; handler: ExportHandler }> = {
    sale: { name: "warehouse_sales_report", handler: reportService.exportWarehouseReport },
    purchase: { name: "warehouse_purchase_report", handler: reportService.exportWarehousePurchaseReport },
    quotation: { name: "warehouse_quotation_report", handler: reportService.exportWarehouseQuotationReport },
    expense: { name: "warehouse_expense_report", handler: reportService.exportWarehouseExpenseReport },
  };

  const exportReport = async () => {
    exporting.value = true;

    try {
      const { handler, name } = exportHandlers[activeTab.value];
      const response = await handler({
        warehouse_id: filters.warehouse_id || undefined,
        from_date: filters.from_date || undefined,
        to_date: filters.to_date || undefined,
        search_query: searchQuery.value || undefined,
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${name}_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting warehouse report:", error);
      Swal.fire({
        icon: "error",
        title: t(`${i18nScope}.export_failed`),
        text: t(`${i18nScope}.unable_to_export_report`),
      });
    } finally {
      exporting.value = false;
    }
  };

  watch(searchQuery, () => {
    currentPage.value = 1;
    fetchReport();
  });

  onMounted(async () => {
    await fetchWarehouses();
    await fetchReport();
  });

  return {
    pageTitle,
    i18nScope,
    activeTab,
    warehouses,
    sales,
    purchases,
    quotations,
    expenses,
    loading,
    exporting,
    searchQuery,
    filters,
    pagination,
    summary,
    setActiveTab,
    handleTableChange,
    applyFilters,
    resetFilters,
    exportReport,
  };
}
